import { useState } from 'react'
import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js'
import useCurrentPage from '@/hooks/use-current-page'
import useI18n from '@/hooks/use-i18n'
import { useFormSubmissionState } from '@/hooks/useFormSubmissionState'

export type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  birthday: string
  location: string
  language: string
}

export type FormErrors = Record<string, string>

/** Maps locale strings (e.g. 'en-ca') to their country code for phone validation. */
function getCountryFromLocale(locale: string): CountryCode {
  const country = locale?.split('-')?.[1]?.toUpperCase()
  if (country === 'CA') return 'CA'
  return 'US'
}

export const useNewsletterForm = (onSuccess?: () => void) => {
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()
  const { loading, formSubmit, formSubmitSuccess, startSubmission, finishSubmission } = useFormSubmissionState()

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: '',
    location: '',
    language: currentLanguage as string,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [submitContext, setSubmitContext] = useState<string | null>(null)

  const handleChange = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = event.target
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }))

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: '',
      }))
    }
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let value = event.target.value

    // Handle backspace and deletion logic
    if (value.length < formData.birthday.length) {
      if (value[value.length - 1] === '/') {
        value = value.slice(0, -1)
      }
      setFormData(prev => ({
        ...prev,
        birthday: value,
      }))
      return
    }

    // Only allow numbers
    value = value.replace(/\D/g, '').slice(0, 8)

    // Format with slashes
    if (value.length >= 4) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`
    } else if (value.length >= 2) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`
    }

    // Validate month (01-12)
    if (value.length >= 2) {
      const month = parseInt(value.slice(0, 2))
      if (month < 1 || month > 12) {
        value = ''
      }
    }

    // Validate day (01-31)
    if (value.length >= 5) {
      const day = parseInt(value.slice(3, 5))
      if (day < 1 || day > 31) {
        value = value.slice(0, 3)
      }
    }

    setFormData(prev => ({
      ...prev,
      birthday: value,
    }))

    if (errors.birthday) {
      setErrors(prev => ({
        ...prev,
        birthday: '',
      }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = i18n('requiredField')
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = i18n('requiredField')
    }
    if (!formData.email.trim()) {
      newErrors.email = i18n('requiredField')
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = i18n('invalidEmailAddress')
    }
    if (formData.phone.trim() && !isValidPhoneNumber(formData.phone.trim(), getCountryFromLocale(currentLanguage as string))) {
      newErrors.phone = i18n('invalidPhoneNumber')
    }
    if (!formData.birthday.trim()) {
      newErrors.birthday = i18n('requiredField')
    }
    if (!formData.location) {
      newErrors.location = i18n('requiredField')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent resubmission
    if (formSubmitSuccess || loading) {
      return
    }

    if (!validateForm()) {
      finishSubmission(false)
      return
    }

    startSubmission()
    try {
      const response = await fetch('/api/newsletter-sign-up-klaviyo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(formData.location && { location: formData.location.trim() }),
        }),
      })

      const data = await response.json()

      // Handle 409 Conflict - user already subscribed to this location.
      // Treated as a soft success (not an error from the user's perspective),
      // consistent with EmailSignupInput behavior.
      if (response.status === 409) {
        setSubmitContext('already_subscribed')
        finishSubmission(true)
        return
      }

      // Handle other error status codes (400, 500, 503)
      if (!response.ok) {
        console.error('Newsletter signup failed:', data?.message || response.statusText)
        setSubmitContext(null)
        finishSubmission(false)
        return
      }

      const context = data?.context
      setSubmitContext(context || null)
      finishSubmission(data?.success === true)

      if (data?.success && onSuccess) {
        onSuccess()
      }
    } catch (error) {
      setSubmitContext(null)
      finishSubmission(false)
      console.error('Newsletter signup failed:', error)
    }
  }

  const memberAlreadySubscribed = submitContext === 'already_subscribed'

  return {
    formData,
    errors,
    loading,
    formSubmitSuccess,
    formSubmit,
    memberAlreadySubscribed,
    submitContext,
    handleChange,
    handleDateChange,
    handleSubmit,
  }
}
